import React, { useState, useEffect, useRef } from "react";
import { FcGoogle } from "react-icons/fc";
import { FiArrowRight, FiSend } from "react-icons/fi";
import {
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  ModalSubtitle,
  TitleContainer,
  ChevronButton,
  GoogleButton,
  LoginIcon,
  LogoWrap,
  SocialColumn,
  Divider,
  DividerText,
  EmailSection,
  EmailInput,
  TurnstileWrapper,
  VerificationMessage,
  CodeInputContainer,
  CodeInput,
  ResendRow,
  ResendButton,
  ResendCountdown,
  EmailPill,
  PrimaryActionButton,
  AuthShowcase,
  AuthShowcaseImage,
  AuthBeforeCard,
  AuthBeforeImage,
  AuthShowcaseContent,
  AuthBenefitList,
  AuthBenefit,
  AuthBenefitIcon,
} from "./AuthModal.styles";
import {
  Droplet,
  SplashLayer,
} from "../ExplosionButton/ExplosionButton.styles";
import { useDispatch, useSelector } from "react-redux";
import { sendCode, verifyEmail } from "../../store/slices/auth/asyncThunks";
import { toast } from "../modern-ui/sonner";
import {
  selectLoginPending,
  selectVerifyEmailLoading,
} from "../../store/slices/auth/slice";
import MailChecker from "mailchecker";
import { selectAffiliateRef } from "../../store/slices/common/slice";
import TurnstileWidget from "../../components/utils/TurnstileWidget";
import { AUTH_TYPE } from "../../utils/constants";
import { useGoogleLogin } from "@react-oauth/google";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as EditSvg } from "../../assets/icons/edit.svg";
import CustomDiscord from "../utils/CustomDiscord/CustomDiscord";
import { useSearchParams } from "react-router-dom";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import TelegramLogin from "./TelegramLoginButton";
import authBeforeImage from "../../assets/images/auth-before.webp";
import authAfterImage from "../../assets/images/auth-after.webp";

// Droplets that fly out of the modal on successful login.
const SUCCESS_DROPLETS = [
  { angle: -100, distance: 130, size: 26, rot: 320, delay: 0, duration: 820 },
  { angle: -55, distance: 115, size: 20, rot: -280, delay: 30, duration: 780 },
  { angle: -10, distance: 140, size: 24, rot: 360, delay: 60, duration: 840 },
  { angle: 30, distance: 110, size: 18, rot: -300, delay: 80, duration: 760 },
  { angle: 75, distance: 105, size: 22, rot: 280, delay: 40, duration: 800 },
  { angle: 130, distance: 120, size: 21, rot: -340, delay: 50, duration: 820 },
  { angle: 175, distance: 135, size: 25, rot: 320, delay: 20, duration: 840 },
  { angle: -135, distance: 112, size: 19, rot: -260, delay: 70, duration: 780 },
];

const buildDropletStyle = ({ angle, distance, rot }) => {
  const rad = (angle * Math.PI) / 180;
  const dx = Math.cos(rad) * distance;
  const dy = Math.sin(rad) * distance;
  return {
    "--dx": `${dx.toFixed(1)}px`,
    "--dy": `${dy.toFixed(1)}px`,
    "--rot": `${rot}deg`,
  };
};

const SUCCESS_BURST_MS = 800;

// Cooldown between verification-code resends, in seconds.
const RESEND_COOLDOWN_SECONDS = 60;

const AuthModal = ({ isOpen, onClose }) => {
  useLockBodyScroll(isOpen);
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [token, setToken] = useState(null);
  const [telegramData, setTelegramData] = useState(null);
  const [isExploding, setIsExploding] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showResendTurnstile, setShowResendTurnstile] = useState(false);

  // Trigger droplet burst over the modal, then close after the splash settles.
  const burstAndClose = (afterClose) => {
    setIsExploding(true);
    setTimeout(() => {
      setIsExploding(false);
      onClose();
      afterClose?.();
    }, SUCCESS_BURST_MS);
  };

  const loginLoading = useSelector(selectLoginPending);
  const verifyEmailLoading = useSelector(selectVerifyEmailLoading);

  const affiliateRef = useSelector(selectAffiliateRef);
  const inputRefs = useRef([]);
  const turnstileRef = useRef(null);

  const discordCodeParam = searchParams.get("code");
  const discordStateParam = searchParams.get("state");

  const dispatch = useDispatch();

  // Tick the resend cooldown down once per second while it is active.
  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleCodeChange = (index, value) => {
    if (value?.length > 1) {
      value = value[0];
    }

    if (!/^\d*$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // if (e.key === "Enter" && code.every((digit) => digit !== "")) {
    //   handleVerifyCode();
    // }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);

    if (!/^\d+$/.test(pastedData)) {
      return;
    }

    const newCode = [...code];
    for (let i = 0; i < pastedData?.length && i < 6; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);

    const nextEmptyIndex = newCode.findIndex((digit) => !digit);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  const onGoogleSignUpClick = useGoogleLogin({
    flow: "auth-code",
    onSuccess: ({ code }) => {
      dispatch(
        sendCode({
          googleCode: code,
          authType: AUTH_TYPE.GOOGLE,
          ...(affiliateRef && { affiliateRef }),
        }),
      )
        .unwrap()
        .then((response) => {
          if (response?.isNewUser) {
            toast.success("Please set your username.");
            burstAndClose(() => setShowCodeInput(false));
          } else {
            toast.success("Successfully signed in with Google!");
            burstAndClose();
          }
        })
        .catch((err) => {
          toast.error(
            err.message || "Google sign-in failed. Please try again.",
          );
        })
        .finally(() => {
          turnstileRef.current?.reset();
          setToken(null);
        });
    },
    onError: (err) => {
      toast.error("Google sign-in failed");
      console.error(err);
    },
  });

  const handleSendCode = () => {
    if (!email) {
      toast.error("Please enter an email address");
      return;
    }

    if (!MailChecker.isValid(email) || email.includes("+")) {
      toast.error("Please enter a valid email address");
      return;
    }

    dispatch(sendCode({ email, token, authType: AUTH_TYPE.BASIC }))
      .unwrap()
      .then(() => {
        setShowCodeInput(true);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        toast.success(`Verification code sent to ${email}`);
      })
      .catch((err) => {
        toast.error(
          err?.message ||
            "Failed to send code. Please try again or use another email.",
        );
      })
      .finally(() => {
        turnstileRef.current?.reset();
        setToken(null);
      });
  };

  useEffect(() => {
    const codeString = code.join("");
    if (codeString.trim()?.length !== 6 || verifyEmailLoading) {
      return;
    }
    dispatch(
      verifyEmail({
        email,
        code: codeString,
        ...(affiliateRef && { affiliateRef }),
      }),
    )
      .unwrap()
      .then((response) => {
        if (response?.data?.isNewUser) {
          toast.success("Code verified! Please set your username.");
          burstAndClose(() => setShowCodeInput(false));
        } else {
          toast.success("Successfully signed in!");
          burstAndClose();
        }
      })
      .catch((err) => {
        toast.error(err?.message || "Invalid code. Please try again.");
      });
    // eslint-disable-next-line
  }, [code]);

  const handleVerifyCode = () => {
    const codeString = code.join("");

    if (codeString?.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }

    if (verifyEmailLoading) {
      return;
    }

    dispatch(
      verifyEmail({
        email,
        code: codeString,
        ...(affiliateRef && { affiliateRef }),
      }),
    )
      .unwrap()
      .then((response) => {
        if (response?.data?.isNewUser) {
          toast.success("Code verified! Please set your username.");
          burstAndClose(() => setShowCodeInput(false));
        } else {
          toast.success("Successfully signed in!");
          burstAndClose();
        }
      })
      .catch((err) => {
        toast.error(err?.message || "Invalid code. Please try again.");
      });
  };

  const handleDiscordError = (error) => {
    toast.error(error || "Discord login failed. Please try again.");
    turnstileRef.current?.reset();
    setToken(null);
  };

  // Clicking "Resend" reveals the Turnstile; the actual request is fired from
  // the widget's onSuccess once verification completes.
  const handleResendClick = () => {
    if (resendCooldown > 0 || showResendTurnstile) {
      return;
    }
    setShowResendTurnstile(true);
  };

  // Sends the code again using the freshly minted Turnstile token, then starts
  // the cooldown so the button can't be spammed.
  const handleResendCode = (resendToken) => {
    setCode(["", "", "", "", "", ""]);
    dispatch(sendCode({ email, token: resendToken, authType: AUTH_TYPE.BASIC }))
      .unwrap()
      .then(() => {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        toast.success(`Verification code resent to ${email}`);
      })
      .catch((err) => {
        toast.error(
          err?.message ||
            "Failed to resend code. Please try again or use another email.",
        );
      })
      .finally(() => {
        setShowResendTurnstile(false);
        turnstileRef.current?.reset();
        setToken(null);
      });
  };

  const handleBackToEmail = () => {
    setShowCodeInput(false);
    setShowResendTurnstile(false);
    setCode(["", "", "", "", "", ""]);
  };

  useEffect(() => {
    if (showCodeInput) {
      const raf = requestAnimationFrame(() => {
        inputRefs.current[0]?.focus();
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [showCodeInput]);

  useEffect(() => {
    if (telegramData) {
      dispatch(
        sendCode({
          telegramData,
          authType: AUTH_TYPE.TELEGRAM,
          ...(affiliateRef && { affiliateRef }),
        }),
      )
        .unwrap()
        .then((response) => {
          if (response?.isNewUser) {
            toast.success("Please set your username.");
            burstAndClose();
          } else {
            toast.success("Successfully signed in!");
            burstAndClose(() => setTelegramData(null));
          }
        })
        .catch((err) => {
          toast.error(
            err?.message || "Telegram sign-in failed. Please try again.",
          );
        })
        .finally(() => {
          setToken(null);
        });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telegramData]);

  useEffect(() => {
    if (discordCodeParam) {
      const finalAffiliate = affiliateRef || discordStateParam;
      dispatch(
        sendCode({
          discordCode: discordCodeParam,
          authType: AUTH_TYPE.DISCORD,
          ...(finalAffiliate && { affiliateRef: finalAffiliate }),
        }),
      )
        .unwrap()
        .then((response) => {
          if (response?.isNewUser) {
            toast.success("Please set your username.");
            burstAndClose();
          } else {
            toast.success("Successfully signed in!");
            burstAndClose();
          }
        })
        .catch((err) => {
          toast.error(
            err?.message || "Discord sign-in failed. Please try again.",
          );
        })
        .finally(() => {
          searchParams.delete("code");
          searchParams.delete("state");
          setSearchParams(searchParams);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discordCodeParam]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={() => {
        setEmail("");
        setCode(["", "", "", "", "", ""]);
        setShowCodeInput(false);
        setShowResendTurnstile(false);
        setResendCooldown(0);
        setToken(null);
      }}
    >
      <ModalContainer>
        {isExploding && (
          <SplashLayer>
            {SUCCESS_DROPLETS.map((d, i) => (
              <Droplet
                key={i}
                $size={d.size}
                $delay={d.delay}
                $duration={d.duration}
                style={buildDropletStyle(d)}
              >
                💦
              </Droplet>
            ))}
          </SplashLayer>
        )}
        <CloseButton
          onClick={() => {
            onClose();
            setTelegramData(null);
          }}
        >
          <CloseSvg />
        </CloseButton>
        {showCodeInput && (
          <ChevronButton onClick={handleBackToEmail}>
            <ChevronSvg />
          </ChevronButton>
        )}
        <ModalContent>
          <LogoWrap aria-hidden>
            <img src="/logos/fapify.webp" alt="Fapify" />
          </LogoWrap>

          {showCodeInput ? (
            <>
              <TitleContainer>
                <ModalTitle>Enter Verification Code</ModalTitle>
                <ModalSubtitle>We sent a 6-digit code to</ModalSubtitle>
                <EmailPill>
                  {email}
                  <EditSvg width={14} height={14} onClick={handleBackToEmail} />
                </EmailPill>
              </TitleContainer>

              <VerificationMessage>
                <CodeInputContainer>
                  {code.map((digit, index) => (
                    <CodeInput
                      key={index}
                      disabled={verifyEmailLoading}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      maxLength={1}
                    />
                  ))}
                </CodeInputContainer>
                <PrimaryActionButton
                  type="button"
                  onClick={handleVerifyCode}
                  disabled={code.some((digit) => !digit) || verifyEmailLoading}
                >
                  {verifyEmailLoading ? (
                    <l-bouncy size="18" speed="0.9" color="#ffffff" />
                  ) : (
                    <>
                      Continue
                      <FiArrowRight />
                    </>
                  )}
                </PrimaryActionButton>
                {showResendTurnstile && (
                  <TurnstileWrapper>
                    <TurnstileWidget
                      ref={turnstileRef}
                      siteKey="0x4AAAAAAC2u6p6YJnRHED8O"
                      onSuccess={(resendToken) => {
                        setToken(resendToken);
                        handleResendCode(resendToken);
                      }}
                      onError={() => {
                        setShowResendTurnstile(false);
                        toast.error("Verification failed");
                      }}
                      onExpire={() => {
                        setToken(null);
                        setShowResendTurnstile(false);
                        toast.error("Verification expired");
                      }}
                    />
                  </TurnstileWrapper>
                )}
                <ResendRow>
                  Didn’t get the code?
                  {resendCooldown > 0 ? (
                    <ResendCountdown>
                      Resend in {resendCooldown}s
                    </ResendCountdown>
                  ) : showResendTurnstile ? (
                    <ResendCountdown>Verifying…</ResendCountdown>
                  ) : (
                    <ResendButton onClick={handleResendClick}>
                      Resend
                    </ResendButton>
                  )}
                </ResendRow>
              </VerificationMessage>
            </>
          ) : (
            <>
              <TitleContainer>
                <ModalTitle>Welcome back</ModalTitle>
                <ModalSubtitle>Sign in to continue to Fapify</ModalSubtitle>
              </TitleContainer>

              {!discordCodeParam && (
                <SocialColumn>
                  <TelegramLogin
                    disabled={loginLoading}
                    onAuth={(data) => {
                      setTelegramData({ ...data });
                    }}
                  />

                  <GoogleButton
                    type="button"
                    onClick={onGoogleSignUpClick}
                    disabled={loginLoading}
                  >
                    <LoginIcon>
                      <FcGoogle size={18} />
                    </LoginIcon>
                    Log in with Google
                  </GoogleButton>

                  <CustomDiscord
                    onError={handleDiscordError}
                    disabled={loginLoading}
                    affiliateRef={affiliateRef}
                  />
                </SocialColumn>
              )}

              {!telegramData && !discordCodeParam && (
                <Divider>
                  <DividerText>or</DividerText>
                </Divider>
              )}

              <EmailSection>
                {!telegramData && !discordCodeParam && (
                  <EmailInput
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSendCode()}
                  />
                )}

                <TurnstileWrapper>
                  <TurnstileWidget
                    ref={turnstileRef}
                    siteKey="0x4AAAAAAC2u6p6YJnRHED8O"
                    onSuccess={(token) => {
                      console.log("Turnstile success token:", token);
                      setToken(token);
                    }}
                    onError={(error) => {
                      console.log("Turnstile error:", error);
                      toast.error("Verification failed");
                    }}
                    onExpire={() => {
                      console.log("Turnstile expired");
                      setToken(null);
                      toast.error("Verification expired");
                    }}
                  />
                </TurnstileWrapper>

                <PrimaryActionButton
                  type="button"
                  onClick={handleSendCode}
                  disabled={!(email || telegramData) || loginLoading}
                >
                  {loginLoading ? (
                    <l-bouncy size="18" speed="0.9" color="#ffffff" />
                  ) : (
                    <>
                      <FiSend />
                      Send Verification Code
                    </>
                  )}
                </PrimaryActionButton>
              </EmailSection>
            </>
          )}
        </ModalContent>

        <AuthShowcase aria-label="Fapify before and after preview">
          <AuthShowcaseImage src={authAfterImage} alt="After preview" />
          <AuthBeforeCard>
            <AuthBeforeImage src={authBeforeImage} alt="Before preview" />
          </AuthBeforeCard>
          <AuthShowcaseContent>
            <AuthBenefitList>
              <AuthBenefit>
                <AuthBenefitIcon aria-hidden>✓</AuthBenefitIcon>
                20 Free credits instantly
              </AuthBenefit>
              <AuthBenefit>
                <AuthBenefitIcon aria-hidden>✓</AuthBenefitIcon>
                More then 3000 templates
              </AuthBenefit>
              <AuthBenefit>
                <AuthBenefitIcon aria-hidden>✓</AuthBenefitIcon>
                Undress any photo
              </AuthBenefit>
              <AuthBenefit>
                <AuthBenefitIcon aria-hidden>✓</AuthBenefitIcon>
                New hotter templates weekly
              </AuthBenefit>
              <AuthBenefit>
                <AuthBenefitIcon aria-hidden>✓</AuthBenefitIcon>
                Videos in 2 minutes
              </AuthBenefit>
              <AuthBenefit>
                <AuthBenefitIcon aria-hidden>✓</AuthBenefitIcon>
                100% Private
              </AuthBenefit>
            </AuthBenefitList>
          </AuthShowcaseContent>
        </AuthShowcase>
      </ModalContainer>
    </Modal>
  );
};

export default AuthModal;
